import Head from 'next/head';
import { useRouter } from 'next/router';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
import { RedesignLayout } from 'root/src/client/v2/layout/features/RedesignLayout/RedesignLayout';
import { Toaster } from 'root/src/client/v2/common/components/ui/sonner';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { useCreatorProfile } from 'root/src/client/v2/_pages/creator/redesign/useCreatorProfile';
import { CreatorProfileHeader } from 'root/src/client/v2/_pages/creator/redesign/CreatorProfileHeader';
import { CreatorRelatedProfiles } from 'root/src/client/v2/_pages/creator/redesign/CreatorRelatedProfiles';
import { CreatorDareClips } from 'root/src/client/v2/_pages/creator/redesign/CreatorDareClips';
import { CreatorGiveawayPanel } from 'root/src/client/v2/_pages/creator/redesign/CreatorGiveawayPanel';
import { CreatorRewardSection } from 'root/src/client/v2/_pages/creator/redesign/CreatorRewardSection';
const CreatorProfileHeaderSkeleton = () => (<div className="glass rounded-2xl border border-solid border-white/[0.08] p-6 sm:p-8">
		<div className="flex flex-col gap-6 sm:flex-row sm:items-start">
			<Skeleton className="h-20 w-20 shrink-0 rounded-2xl"/>
			<div className="flex-1 space-y-3">
				<Skeleton className="h-9 w-48 rounded-lg"/>
				<Skeleton className="h-4 w-60 rounded"/>
				<div className="flex gap-3 pt-2">
					<Skeleton className="h-9 w-28 rounded-xl"/>
					<Skeleton className="h-9 w-28 rounded-xl"/>
				</div>
			</div>
		</div>
	</div>);
export const RedesignCreatorProfilePage = () => {
    const router = useRouter();
    const recordId = typeof router.query.recordId === 'string' ? router.query.recordId : undefined;
    const { creator, isLoading, isError, refetch } = useCreatorProfile(recordId);
    // Navigates to the legacy create-project flow, deep-linking the creator so autoPopulateDare can
    // pre-fill the assignee + suggested game (twitch/youtube; other platforms open the form unseeded).
    const createProjectHref = (creator === null || creator === void 0 ? void 0 : creator.platform) && (creator === null || creator === void 0 ? void 0 : creator.mnemonicId)
        ? `/create-project?creatorPlatform=${encodeURIComponent(creator.platform)}&creatorMnemonicId=${encodeURIComponent(creator.mnemonicId)}`
        : '/create-project';
    // The legacy record module used to populate the page <title> from the creator name. It no longer
    // mounts on this route (redesign served in place), so the page owns its own title/meta here; the
    // page-level Head overrides the generic default emitted by _app for this route.
    const creatorName = creator === null || creator === void 0 ? void 0 : creator.displayName;
    const pageTitle = creatorName ? `Dare Drop: ${creatorName}'s Profile` : 'Dare Drop: Creator Profile';
    const pageDescription = creatorName
        ? `Watch ${creatorName}'s Completed Dares`
        : 'Dare your favorite creator to do something awesome.';
    return (<RedesignLayout>
			<Head>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} key="metaDescription"/>
			</Head>
			<section className="mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 lg:px-8">
				{isLoading && <CreatorProfileHeaderSkeleton />}

				{!isLoading && (isError || !creator) && (<div className="glass rounded-2xl border border-solid border-white/[0.08] p-10 text-center">
						<h1 className="font-display text-xl font-bold text-foreground">Creator not found</h1>
						<p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
							We could not load this creator profile. Check the link and try again.
						</p>
					</div>)}

				{!isLoading && !isError && creator && (<>
						{creator.isBetaTester ? (<div className="grid gap-6 lg:grid-cols-5">
								<div className="lg:col-span-3">
									<CreatorProfileHeader creator={creator} recordId={recordId} onFollowChanged={refetch}/>
								</div>
								<div className="lg:col-span-2">
									<CreatorGiveawayPanel creator={creator}/>
								</div>
							</div>) : (<CreatorProfileHeader creator={creator} recordId={recordId} onFollowChanged={refetch}/>)}
						{creator.isBetaTester && <CreatorRewardSection creator={creator}/>}
						<CreatorRelatedProfiles profiles={creator.relatedProfiles}/>
						{recordId && (<CreatorDareClips recordId={recordId} isOwnProfile={Boolean(creator.userId) && getCookie('userId') === creator.userId} createProjectHref={createProjectHref}/>)}
					</>)}
			</section>
			<Toaster />
		</RedesignLayout>);
};
